import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { ClauseTemplateItem } from './components';
import { ClauseTemplate } from './clauseTemplateSidebarContainer';
import SearchInput from '../../../../search-input';
import CreateClauseSlideout from '../../../../clause-creation/CreateClauseSlideout';
import styles from './styles.module.scss';

interface ClauseTemplatesSidebarSidebarProps {
  clauseTemplates: ClauseTemplate[];
  isDisabled: boolean;
  teamId?: string | null | undefined;
  onClauseCreated?: () => void;
  // Template editors and managers may draft clauses; publishing is gated in
  // the slideout.
  canCreateClauses?: boolean;
}

const ClauseTemplatesSidebar = ({
  clauseTemplates,
  isDisabled,
  teamId,
  onClauseCreated,
  canCreateClauses,
}: ClauseTemplatesSidebarSidebarProps) => {
  const { formatMessage } = useIntl();
  const [filterText, setFilterText] = useState('');
  const [isCreateClauseSlideoutOpen, setIsCreateClauseSlideoutOpen] = useState(false);

  const openCreateClauseSlideout = () => {
    setIsCreateClauseSlideoutOpen(true);
  };

  const handleClauseCreated = () => {
    // Trigger refresh of clause list
    onClauseCreated?.();
  };

  const handleSlideoutClose = () => {
    setIsCreateClauseSlideoutOpen(false);
  };

  const renderClauseTemplatesItems = useMemo(
    () =>
      clauseTemplates
        .filter((clauseTemplate) => {
          const filterTextLower = filterText?.toLocaleLowerCase();
          return clauseTemplate.title.toLocaleLowerCase().includes(filterTextLower);
        })
        .map((clauseTemplate, index) => (
          <ClauseTemplateItem
            title={clauseTemplate.title}
            key={clauseTemplate.id}
            index={index}
            id={clauseTemplate.id}
            body={clauseTemplate.body}
            format={clauseTemplate.format}
          />
        )),
    [clauseTemplates, filterText]
  );

  return (
    <div className={classNames(styles.wrapper, { [styles['wrapper-disabled']]: isDisabled })}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0 }}>Clauses</h2>
        {!!clauseTemplates.length && teamId && !isDisabled && canCreateClauses && (
          <button
            type="button"
            onClick={openCreateClauseSlideout}
            className="btn btn-sm"
            style={{
              fontSize: '1rem',
              fontWeight: 'bold',
              padding: '0',
              width: '28px',
              height: '28px',
              backgroundColor: '#006c90',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#005a7a';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#006c90';
            }}
            title="Create new clause"
          >
            +
          </button>
        )}
      </div>
      <SearchInput
        className={styles.searchInput}
        value={filterText}
        onChange={(event: any) => setFilterText(event.currentTarget.value)}
        placeholder={formatMessage({
          id: 'clauseTemplates.filter.title',
          defaultMessage: 'Search clause by name',
        })}
      />
      {!!clauseTemplates.length && !renderClauseTemplatesItems.length && (
        <p className="text-center mt-5">No clauses match your filters </p>
      )}
      <div id="clause-templates-container" className={styles['templates-container']}>
        {!!clauseTemplates.length && <>{renderClauseTemplatesItems}</>}
        {!clauseTemplates.length && (
          <>
            <p className="text-center">No clauses available </p>
            {teamId && !isDisabled && canCreateClauses && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={openCreateClauseSlideout}
                  className="btn btn-link font-weight-bold p-0"
                  style={{ textDecoration: 'none' }}
                >
                  <FormattedMessage id="cta.go-to.overview" defaultMessage="Add a Clause Now" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Clause Slideout */}
      <CreateClauseSlideout
        show={isCreateClauseSlideoutOpen}
        onHide={handleSlideoutClose}
        teamId={teamId}
        onClauseCreated={handleClauseCreated}
      />
    </div>
  );
};

export default ClauseTemplatesSidebar;
